import React from 'react';
import { dispositionMeta } from '@/lib/dispositions';
import { TICKETS } from '@/constants/testIds';
import { Loader2 } from 'lucide-react';

function aging(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const diffDays = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  return diffDays === 0 ? 'today' : `${diffDays}d`;
}

export default function TicketsTable({ tickets, loading, lookups, onSelect, emptyTitle, emptyText }) {
  if (loading) {
    return (
      <div className="p-12 text-center text-stone-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      </div>
    );
  }
  if (!tickets || tickets.length === 0) {
    return (
      <div className="p-12 text-center text-sm text-stone-500 max-w-md mx-auto">
        <div className="font-heading text-lg text-stone-700 mb-2">{emptyTitle || 'No tickets yet'}</div>
        <p className="text-stone-500">
          {emptyText || <>Trigger a <strong>Sync from Fundle</strong> in the header to import all existing tickets from your SupportHub CRM.</>}
        </p>
      </div>
    );
  }

  return (
    <table className="w-full text-sm bg-white" data-testid={TICKETS.table}>
      <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
        <tr>
          {['ID', 'Patient', 'Status', 'Source', 'Sub Source', 'Clinic', 'Audiologist', 'Appointment', 'Assignee', 'Aging'].map((h) => (
            <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tickets.map((t) => {
          const m = dispositionMeta(t.disposition);
          const Icon = m.icon;
          return (
            <tr
              key={t.id}
              data-testid={TICKETS.row(t.id)}
              onClick={() => onSelect(t.id)}
              className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer"
            >
              <td className="px-3 py-2 font-mono text-xs text-stone-600 tabular-nums">
                {t.ticket_no || `#${t.id?.slice(-6)}`}
              </td>
              <td className="px-3 py-2">
                <div className="font-medium text-stone-900">{t.patient_name || t.subject || '—'}</div>
                <div className="text-xs text-stone-500">{t.subject || ''}</div>
              </td>
              <td className="px-3 py-2">
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border"
                  style={{ backgroundColor: m.bg, color: m.text, borderColor: m.border }}
                >
                  <Icon className="w-2.5 h-2.5" />
                  {m.label}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-stone-700">{t.source_name || '—'}</td>
              <td className="px-3 py-2 text-xs text-stone-700">{t.subsource_name || '—'}</td>
              <td className="px-3 py-2 text-xs text-stone-700">
                {lookups.clinic[t.clinic_id]?.name || t.clinic_id || '—'}
              </td>
              <td className="px-3 py-2 text-xs text-stone-700">
                {lookups.employee[t.audiologist_id]?.full_name || t.audiologist_name || t.created_by_name || '—'}
              </td>
              <td className="px-3 py-2 text-xs text-stone-700 tabular-nums">
                {t.appointment_ts ? new Date(t.appointment_ts).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '—'}
              </td>
              <td className="px-3 py-2 text-xs text-stone-700">
                {t.assignee_name || (lookups.user[t.assignee_id]?.full_name) || <span className="text-amber-700">Unassigned</span>}
              </td>
              <td className="px-3 py-2 text-xs text-stone-500 tabular-nums">
                {aging(t.created_at || t.appointment_ts)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
